package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.layout.engine.Dimension
import top.wcpe.allincore.layout.engine.LayoutMode
import top.wcpe.allincore.ui.runtime.event.MouseEvent
import top.wcpe.allincore.ui.runtime.event.UiEventHandler
import top.wcpe.allincore.ui.runtime.event.UiEventPredicate
import top.wcpe.allincore.ui.runtime.style.CornerRadii
import kotlin.jvm.JvmStatic

/**
 * 物品槽位元素：承载库存槽位的视觉样式（边框 / 圆角 / 状态覆盖 / 背景 / 数量徽标）、
 * 静态物品、交互控制约束与事件 binding。纯配置数据 holder，绘制由各 Slot*Renderer 完成。
 */
@Suppress("TooManyFunctions", "MagicNumber")
class Slot private constructor() : UIElement<Slot>() {

    private var groupIdField = ""
    private var keyField = ""
    private var placeholderField = ""
    private var slotSizeField = 16f
    private var readIdField: String? = null
    private var staticItemField: ItemStack? = null

    private var itemSizeField: Float? = null
    private var itemInsetXField: Float? = null
    private var itemInsetYField: Float? = null
    private var quickCraftPreviewAlphaField: Float? = null

    private var borderColorField: Int? = null
    private var borderWidthField: Float? = null
    private var cornerRadiusField: Float? = null
    private var cornerRadiiField: CornerRadii? = null

    private var selectedField: Boolean? = null
    private var selectedOutlineColorField: Int? = null
    private var selectedOutlineWidthField: Float? = null
    private var selectedOverlayColorField: Int? = null

    private var hoverOverlayColorField: Int? = null
    private var hoverOverlayOpacityField: Float? = null
    private var hoverTextureField: String? = null

    private var pressedOverlayColorField: Int? = null
    private var pressedOverlayOpacityField: Float? = null
    private var pressedTextureField: String? = null

    private var focusOverlayColorField: Int? = null
    private var focusBorderColorField: Int? = null
    private var focusTextureField: String? = null

    private var disabledOpacityField: Float? = null
    private var disabledOverlayColorField: Int? = null
    private var disabledTextureField: String? = null

    private var warnOverlayColorField: Int? = null

    private var successColorField: Int? = null
    private var failedColorField: Int? = null
    private var timeoutColorField: Int? = null

    private var backgroundBorderWidthField: Float? = null

    private var backgroundTextureField: String? = null
    private var hoverBackgroundTextureField: String? = null
    private var pressedBackgroundTextureField: String? = null
    private var focusBackgroundTextureField: String? = null
    private var disabledBackgroundTextureField: String? = null

    private var showBackgroundField: Boolean? = false
    private var backgroundColorField: Int? = null
    private var placeholderInsetField: Float? = null
    private var showTooltipField: Boolean? = true
    private var showCountField: Boolean? = true
    private var countAnchorField = "bottom-right"
    private var countOffsetXField: Float? = null
    private var countOffsetYField: Float? = null

    // 交互控制（客户端 UI 约束，非安全契约——作弊客户端可绕过，真正禁止须在 SlotProvider 服务端 override）。
    private var readOnlyField = false
    private var allowShiftTransferField = true
    private var allowDropField = true
    private var allowPickField = true
    private var slotTypeField = ""

    /** null = 继承 groupId 默认；非 null 显式覆盖。 */
    private var predictField: Boolean? = null

    // 事件 binding 字符串引用
    private var onRightClickField = ""
    private var onLongPressField = ""
    private var onHoverField = ""
    private var onDragEnterField = ""
    private var onDragLeaveField = ""
    private var interceptClickField = ""
    private var interceptRightClickField = ""
    private var interceptDropField = ""

    // 运行时事件回调
    private var onRightClickHandlerField: UiEventHandler<MouseEvent>? = null
    private var onLongPressHandlerField: UiEventHandler<MouseEvent>? = null
    private var onHoverHandlerField: UiEventHandler<MouseEvent>? = null
    private var onDragEnterHandlerField: UiEventHandler<MouseEvent>? = null
    private var onDragLeaveHandlerField: UiEventHandler<MouseEvent>? = null
    private var interceptClickHandlerField: UiEventPredicate<MouseEvent>? = null
    private var interceptRightClickHandlerField: UiEventPredicate<MouseEvent>? = null
    private var interceptDropHandlerField: UiEventPredicate<MouseEvent>? = null

    init {
        // 支持子组件布局（默认 COLUMN，子组件从上到下排列，物品在最上层可点击）
        styleField.layout().mode = LayoutMode.COLUMN
        // 命中盒尺寸 = slotSize（默认 16）
        layoutStyle().width = Dimension.px(slotSizeField)
        layoutStyle().height = Dimension.px(slotSizeField)
    }

    override fun self(): Slot = this

    // ========== fluent setters ==========

    fun groupId(groupId: String?): Slot = apply { groupIdField = groupId ?: "" }
    fun key(key: String?): Slot = apply { keyField = key ?: "" }
    fun placeholder(placeholder: String?): Slot = apply { placeholderField = placeholder ?: "" }
    fun readId(readId: String?): Slot = apply { readIdField = readId }
    fun readId(): String? = readIdField

    fun slotSize(slotSize: Float): Slot = apply {
        slotSizeField = slotSize
        layoutStyle().width = Dimension.px(slotSize)
        layoutStyle().height = Dimension.px(slotSize)
    }

    // === 静态物品 ===
    fun staticItem(item: ItemStack?): Slot = apply { staticItemField = item }
    fun staticItem(): ItemStack? = staticItemField

    // === 几何覆盖 ===
    fun itemSize(v: Float?): Slot = apply { itemSizeField = v }
    fun itemInsetX(v: Float?): Slot = apply { itemInsetXField = v }
    fun itemInsetY(v: Float?): Slot = apply { itemInsetYField = v }
    fun quickCraftPreviewAlpha(v: Float?): Slot = apply { quickCraftPreviewAlphaField = v }

    // === 边框圆角 ===
    fun borderColor(v: Int?): Slot = apply { borderColorField = v }
    fun borderWidth(v: Float?): Slot = apply { borderWidthField = v }

    fun cornerRadius(v: Float?): Slot = apply {
        cornerRadiusField = v
        cornerRadiiField = v?.let { CornerRadii.uniform(it) }
    }

    fun cornerRadii(v: CornerRadii?): Slot = apply {
        cornerRadiiField = v
        cornerRadiusField = v?.tl
    }

    fun cornerRadii(): CornerRadii? = cornerRadiiField

    // === 选中状态 ===
    fun selected(v: Boolean): Slot = apply { selectedField = v }
    fun selected(v: Boolean?): Slot = apply { selectedField = v }
    fun selected(): Boolean = selectedField == true
    fun selectedOutlineColor(v: Int?): Slot = apply { selectedOutlineColorField = v }
    fun selectedOutlineWidth(v: Float?): Slot = apply { selectedOutlineWidthField = v }
    fun selectedOverlayColor(v: Int?): Slot = apply { selectedOverlayColorField = v }

    // === 状态视觉覆盖 ===
    fun hoverOverlayColor(v: Int?): Slot = apply { hoverOverlayColorField = v }
    fun hoverOverlayOpacity(v: Float?): Slot = apply { hoverOverlayOpacityField = v }
    fun hoverTexture(v: String?): Slot = apply { hoverTextureField = v }

    fun pressedOverlayColor(v: Int?): Slot = apply { pressedOverlayColorField = v }
    fun pressedOverlayOpacity(v: Float?): Slot = apply { pressedOverlayOpacityField = v }
    fun pressedTexture(v: String?): Slot = apply { pressedTextureField = v }

    fun focusOverlayColor(v: Int?): Slot = apply { focusOverlayColorField = v }
    fun focusBorderColor(v: Int?): Slot = apply { focusBorderColorField = v }
    fun focusTexture(v: String?): Slot = apply { focusTextureField = v }

    fun disabledOpacity(v: Float?): Slot = apply { disabledOpacityField = v }
    fun disabledOverlayColor(v: Int?): Slot = apply { disabledOverlayColorField = v }
    fun disabledTexture(v: String?): Slot = apply { disabledTextureField = v }

    fun warnOverlayColor(v: Int?): Slot = apply { warnOverlayColorField = v }

    // === 反馈动画色覆盖 ===
    fun successColor(v: Int?): Slot = apply { successColorField = v }
    fun failedColor(v: Int?): Slot = apply { failedColorField = v }
    fun timeoutColor(v: Int?): Slot = apply { timeoutColorField = v }

    // === 背景内缩边框宽度覆盖 ===
    fun backgroundBorderWidth(v: Float?): Slot = apply { backgroundBorderWidthField = v }

    // === 纹理背景 ===
    fun backgroundTexture(v: String?): Slot = apply { backgroundTextureField = v }
    fun hoverBackgroundTexture(v: String?): Slot = apply { hoverBackgroundTextureField = v }
    fun pressedBackgroundTexture(v: String?): Slot = apply { pressedBackgroundTextureField = v }
    fun focusBackgroundTexture(v: String?): Slot = apply { focusBackgroundTextureField = v }
    fun disabledBackgroundTexture(v: String?): Slot = apply { disabledBackgroundTextureField = v }

    // === 基础控制 ===
    fun showBackground(v: Boolean?): Slot = apply { showBackgroundField = v }
    fun showBackground(): Boolean = showBackgroundField == true
    fun backgroundColor(v: Int?): Slot = apply { backgroundColorField = v }
    fun placeholderInset(v: Float?): Slot = apply { placeholderInsetField = v }

    /** showTooltip 默认 true（显示原版 tooltip），设为 false 关闭。 */
    fun showTooltip(v: Boolean?): Slot = apply { showTooltipField = v }

    /** showTooltip getter：null 等价 true（默认显示）。 */
    fun showTooltip(): Boolean = showTooltipField != false

    /** showCount 默认 true（显示数量徽标），设为 false 隐藏。 */
    fun showCount(v: Boolean?): Slot = apply { showCountField = v }

    /** showCount getter：null 等价 true（默认显示）。 */
    fun showCount(): Boolean = showCountField != false

    /** 数量徽标锚点；null/空串回退到 "bottom-right"（vanilla 右下角）。 */
    fun countAnchor(v: String?): Slot = apply { countAnchorField = if (v.isNullOrEmpty()) "bottom-right" else v }
    fun countAnchor(): String = countAnchorField
    fun countOffsetX(v: Float?): Slot = apply { countOffsetXField = v }
    fun countOffsetX(): Float? = countOffsetXField
    fun countOffsetY(v: Float?): Slot = apply { countOffsetYField = v }
    fun countOffsetY(): Float? = countOffsetYField

    // === 交互控制 ===
    fun readOnly(v: Boolean): Slot = apply { readOnlyField = v }
    fun allowShiftTransfer(v: Boolean): Slot = apply { allowShiftTransferField = v }
    fun allowDrop(v: Boolean): Slot = apply { allowDropField = v }
    fun allowPick(v: Boolean): Slot = apply { allowPickField = v }
    fun slotType(v: String?): Slot = apply { slotTypeField = v ?: "" }

    // === 预测开关 ===
    fun predict(v: Boolean?): Slot = apply { predictField = v }
    fun predict(): Boolean? = predictField

    // ========== getters ==========

    fun groupId(): String = groupIdField
    fun key(): String = keyField
    fun placeholder(): String = placeholderField
    fun slotSize(): Float = slotSizeField

    fun itemSize(): Float? = itemSizeField
    fun itemInsetX(): Float? = itemInsetXField
    fun itemInsetY(): Float? = itemInsetYField
    fun quickCraftPreviewAlpha(): Float? = quickCraftPreviewAlphaField

    fun borderColor(): Int? = borderColorField
    fun borderWidth(): Float? = borderWidthField
    fun cornerRadius(): Float? = cornerRadiusField

    fun selectedOutlineColor(): Int? = selectedOutlineColorField
    fun selectedOutlineWidth(): Float? = selectedOutlineWidthField
    fun selectedOverlayColor(): Int? = selectedOverlayColorField

    fun hoverOverlayColor(): Int? = hoverOverlayColorField
    fun hoverOverlayOpacity(): Float? = hoverOverlayOpacityField
    fun hoverTexture(): String? = hoverTextureField

    fun pressedOverlayColor(): Int? = pressedOverlayColorField
    fun pressedOverlayOpacity(): Float? = pressedOverlayOpacityField
    fun pressedTexture(): String? = pressedTextureField

    fun focusOverlayColor(): Int? = focusOverlayColorField
    fun focusBorderColor(): Int? = focusBorderColorField
    fun focusTexture(): String? = focusTextureField

    fun disabledOpacity(): Float? = disabledOpacityField
    fun disabledOverlayColor(): Int? = disabledOverlayColorField
    fun disabledTexture(): String? = disabledTextureField

    fun warnOverlayColor(): Int? = warnOverlayColorField

    fun successColor(): Int? = successColorField
    fun failedColor(): Int? = failedColorField
    fun timeoutColor(): Int? = timeoutColorField

    fun backgroundBorderWidth(): Float? = backgroundBorderWidthField

    fun backgroundTexture(): String? = backgroundTextureField
    fun hoverBackgroundTexture(): String? = hoverBackgroundTextureField
    fun pressedBackgroundTexture(): String? = pressedBackgroundTextureField
    fun focusBackgroundTexture(): String? = focusBackgroundTextureField
    fun disabledBackgroundTexture(): String? = disabledBackgroundTextureField

    fun backgroundColor(): Int? = backgroundColorField
    fun placeholderInset(): Float? = placeholderInsetField

    fun readOnly(): Boolean = readOnlyField
    fun allowShiftTransfer(): Boolean = allowShiftTransferField
    fun allowDrop(): Boolean = allowDropField
    fun allowPick(): Boolean = allowPickField
    fun slotType(): String = slotTypeField

    // ========== 事件 binding ==========

    fun onRightClick(v: String?): Slot = apply { onRightClickField = v ?: "" }
    fun onRightClick(): String = onRightClickField

    fun onLongPress(v: String?): Slot = apply { onLongPressField = v ?: "" }
    fun onLongPress(): String = onLongPressField

    fun onHover(v: String?): Slot = apply { onHoverField = v ?: "" }
    fun onHover(): String = onHoverField

    fun onDragEnter(v: String?): Slot = apply { onDragEnterField = v ?: "" }
    fun onDragEnter(): String = onDragEnterField

    fun onDragLeave(v: String?): Slot = apply { onDragLeaveField = v ?: "" }
    fun onDragLeave(): String = onDragLeaveField

    fun interceptClick(v: String?): Slot = apply { interceptClickField = v ?: "" }
    fun interceptClick(): String = interceptClickField

    fun interceptRightClick(v: String?): Slot = apply { interceptRightClickField = v ?: "" }
    fun interceptRightClick(): String = interceptRightClickField

    fun interceptDrop(v: String?): Slot = apply { interceptDropField = v ?: "" }
    fun interceptDrop(): String = interceptDropField

    // ========== 运行时事件回调 ==========

    fun onRightClickHandler(h: UiEventHandler<MouseEvent>?): Slot = apply { onRightClickHandlerField = h }
    override fun onRightClickHandler(): UiEventHandler<MouseEvent>? = onRightClickHandlerField

    fun onDragEnterHandler(h: UiEventHandler<MouseEvent>?): Slot = apply { onDragEnterHandlerField = h }
    override fun onDragEnterHandler(): UiEventHandler<MouseEvent>? = onDragEnterHandlerField

    fun onDragLeaveHandler(h: UiEventHandler<MouseEvent>?): Slot = apply { onDragLeaveHandlerField = h }
    override fun onDragLeaveHandler(): UiEventHandler<MouseEvent>? = onDragLeaveHandlerField

    fun onLongPressHandler(h: UiEventHandler<MouseEvent>?): Slot = apply { onLongPressHandlerField = h }
    override fun onLongPressHandler(): UiEventHandler<MouseEvent>? = onLongPressHandlerField

    fun onHoverHandler(h: UiEventHandler<MouseEvent>?): Slot = apply { onHoverHandlerField = h }
    override fun onHoverHandler(): UiEventHandler<MouseEvent>? = onHoverHandlerField

    fun interceptClickHandler(h: UiEventPredicate<MouseEvent>?): Slot = apply { interceptClickHandlerField = h }
    fun interceptClickHandler(): UiEventPredicate<MouseEvent>? = interceptClickHandlerField

    fun interceptRightClickHandler(h: UiEventPredicate<MouseEvent>?): Slot =
        apply { interceptRightClickHandlerField = h }
    fun interceptRightClickHandler(): UiEventPredicate<MouseEvent>? = interceptRightClickHandlerField

    fun interceptDropHandler(h: UiEventPredicate<MouseEvent>?): Slot = apply { interceptDropHandlerField = h }
    fun interceptDropHandler(): UiEventPredicate<MouseEvent>? = interceptDropHandlerField

    companion object {
        @JvmStatic
        fun create(): Slot = Slot()
    }
}
